// Note: Import toast from your preferred toast library
// Example: import { toast } from 'react-hot-toast';
// For now, using console as fallback
const toast = {
  error: (message: string, options?: any) => {
    console.error('Toast Error:', message);
    // In a real implementation, you would show a toast notification
  },
  success: (message: string, options?: any) => {
    console.log('Toast Success:', message);
  },
  warning: (message: string, options?: any) => {
    console.warn('Toast Warning:', message);
  },
  info: (message: string, options?: any) => {
    console.info('Toast Info:', message);
  }
};

// Error types for better categorization
export enum ErrorType {
  NETWORK = 'NETWORK',
  AUTHENTICATION = 'AUTHENTICATION', 
  AUTHORIZATION = 'AUTHORIZATION',
  VALIDATION = 'VALIDATION',
  SERVER = 'SERVER',
  CLIENT = 'CLIENT',
  TIMEOUT = 'TIMEOUT',
  UNKNOWN = 'UNKNOWN'
}

// Enhanced error class with additional context
export class AppError extends Error {
  public readonly type: ErrorType;
  public readonly statusCode?: number;
  public readonly details?: any;
  public readonly timestamp: Date;
  public readonly userMessage: string;

  constructor(
    message: string,
    type: ErrorType = ErrorType.UNKNOWN,
    statusCode?: number,
    details?: any,
    userMessage?: string
  ) {
    super(message);
    this.name = 'AppError';
    this.type = type;
    this.statusCode = statusCode;
    this.details = details;
    this.timestamp = new Date();
    this.userMessage = userMessage || this.getDefaultUserMessage(type);

    // Maintains proper stack trace for where our error was thrown
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, AppError);
    }
  }

  private getDefaultUserMessage(type: ErrorType): string {
    switch (type) {
      case ErrorType.NETWORK:
        return 'Unable to connect. Please check your internet connection and try again.';
      case ErrorType.AUTHENTICATION:
        return 'Please sign in to continue your journey.';
      case ErrorType.AUTHORIZATION:
        return 'You don\'t have permission to access this resource.';
      case ErrorType.VALIDATION:
        return 'Please check your input and try again.';
      case ErrorType.SERVER:
        return 'Server error occurred. Please try again in a moment.';
      case ErrorType.TIMEOUT:
        return 'Request timed out. Please try again.';
      default:
        return 'An unexpected error occurred. Please try again.';
    }
  }
}

// API response type
interface APIResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  statusCode?: number;
}

// Enhanced fetch wrapper with comprehensive error handling
export async function apiRequest<T = any>(
  url: string,
  options: RequestInit = {},
  timeout: number = 30000
): Promise<T> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    clearTimeout(timeoutId);

    // Handle different HTTP status codes
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      
      switch (response.status) {
        case 400:
          throw new AppError(
            'Bad Request: ' + (errorData.error || response.statusText),
            ErrorType.VALIDATION,
            400,
            errorData
          );
        case 401:
          throw new AppError(
            'Authentication required',
            ErrorType.AUTHENTICATION,
            401,
            errorData,
            'Your session has expired. Please sign in again.'
          );
        case 403:
          throw new AppError(
            'Access forbidden',
            ErrorType.AUTHORIZATION,
            403,
            errorData
          );
        case 404:
          throw new AppError(
            'Resource not found',
            ErrorType.CLIENT,
            404,
            errorData,
            'The requested resource could not be found.'
          );
        case 429:
          throw new AppError(
            'Too many requests',
            ErrorType.CLIENT,
            429,
            errorData,
            'Too many requests. Please wait a moment and try again.'
          );
        case 500:
          throw new AppError(
            'Internal server error',
            ErrorType.SERVER,
            500,
            errorData
          );
        default:
          throw new AppError(
            'HTTP ' + response.status + ': ' + response.statusText,
            ErrorType.SERVER,
            response.status,
            errorData
          );
      }
    }

    const data: APIResponse<T> = await response.json();
    
    if (!data.success && data.error) {
      throw new AppError(
        data.error,
        ErrorType.SERVER,
        data.statusCode || response.status,
        data
      );
    }

    return data.data || data;
  } catch (error) {
    clearTimeout(timeoutId);

    // Handle abort/timeout
    if (error instanceof Error && error.name === 'AbortError') {
      throw new AppError(
        'Request timeout',
        ErrorType.TIMEOUT,
        408,
        null,
        'The request took too long. Please try again.'
      );
    }

    // Handle network errors
    if (error instanceof TypeError && error.message.includes('fetch')) {
      throw new AppError(
        'Network error',
        ErrorType.NETWORK,
        0,
        error
      );
    }

    // Re-throw AppError instances
    if (error instanceof AppError) {
      throw error;
    }

    // Handle unknown errors
    throw new AppError(
      error instanceof Error ? error.message : 'Unknown error occurred',
      ErrorType.UNKNOWN,
      undefined,
      error
    );
  }
}

// Error logging utility
export function logError(error: Error | AppError, context?: string): void {
  const errorInfo = {
    message: error.message,
    stack: error.stack,
    timestamp: new Date().toISOString(),
    context,
    url: window.location.href,
    userAgent: navigator.userAgent,
    ...(error instanceof AppError && {
      type: error.type,
      statusCode: error.statusCode,
      details: error.details,
    }),
  };

  // Log to console in development
  if (process.env.NODE_ENV === 'development') {
    console.error('Error logged:', errorInfo);
  }

  // In production, send to monitoring service
  if (process.env.NODE_ENV === 'production') {
    // Example: Send to Sentry, LogRocket, or custom logging service
    // Sentry.captureException(error, { extra: errorInfo });
    console.error('Production error:', errorInfo);
  }
}

// User-friendly error notification
export function handleError(error: Error | AppError, context?: string): void {
  logError(error, context);

  const userMessage = error instanceof AppError 
    ? error.userMessage 
    : 'An unexpected error occurred. Please try again.';

  // Show toast notification
  toast.error(userMessage, {
    duration: 5000,
    action: {
      label: 'Dismiss',
      onClick: () => {},
    },
  });

  // Handle specific error types
  if (error instanceof AppError) {
    switch (error.type) {
      case ErrorType.AUTHENTICATION:
        // Redirect to login after showing error
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
        break;
      
      case ErrorType.NETWORK:
        // Could trigger offline mode or retry mechanism
        break;
        
      case ErrorType.SERVER:
        // Could show maintenance page or server status
        break;
    }
  }
}

// Retry utility with exponential backoff
export async function retryRequest<T>(
  requestFn: () => Promise<T>,
  maxRetries: number = 3,
  baseDelay: number = 1000
): Promise<T> {
  let lastError: Error;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await requestFn();
    } catch (error) {
      lastError = error as Error;
      
      // Don't retry on certain errors
      if (error instanceof AppError) {
        if ([ErrorType.AUTHENTICATION, ErrorType.AUTHORIZATION, ErrorType.VALIDATION].includes(error.type)) {
          throw error;
        }
      }

      // Don't retry on final attempt
      if (attempt === maxRetries) {
        throw error;
      }

      // Exponential backoff delay
      const delay = baseDelay * Math.pow(2, attempt);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  throw lastError!;
}

// Form validation error handler
export function handleFormError(error: unknown, setError: (field: string, error: any) => void): void {
  if (error instanceof AppError && error.type === ErrorType.VALIDATION && error.details?.fields) {
    // Handle field-specific validation errors
    Object.entries(error.details.fields).forEach(([field, message]) => {
      setError(field, { message });
    });
  } else {
    // Handle general form error
    setError('root', { 
      message: error instanceof AppError ? error.userMessage : 'Form submission failed. Please try again.'
    });
  }
}

// Session error recovery
export function handleSessionError(): void {
  // Clear potentially corrupted session data
  sessionStorage.clear();
  
  // Clear local storage related to auth
  Object.keys(localStorage).forEach(key => {
    if (key.startsWith('dragonfly-') || key.includes('auth') || key.includes('session')) {
      localStorage.removeItem(key);
    }
  });

  // Show user-friendly message and redirect
  toast.error('Your session has expired. Redirecting to sign in...', {
    duration: 3000,
  });

  setTimeout(() => {
    window.location.href = '/login?session_expired=true';
  }, 2000);
}

// Global error handler setup
export function setupGlobalErrorHandling(): void {
  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    console.error('Unhandled promise rejection:', event.reason);
    
    const error = event.reason instanceof Error 
      ? event.reason 
      : new Error('Unhandled promise rejection');
    
    logError(error, 'unhandledrejection');
    
    // Prevent default browser behavior
    event.preventDefault();
  });

  // Handle global JavaScript errors
  window.addEventListener('error', (event) => {
    console.error('Global error:', event.error);
    logError(event.error || new Error(event.message), 'global');
  });
}